/**
 * Module: HomeHeader
 * Role: Sticky glass header for the home screen — Awardy mark+wordmark (left),
 *       «копилка N ₽» balance pill + notifications bell (right). Content scrolls under it.
 *       Design source: ai/designs/miniapp/project/screens.jsx:69-112 (canonical home).
 *       Home-specific (fetches balance, navigates) — NOT an atom. The generic PageHeader
 *       atom (logo + bell only) stays untouched for the other screens.
 *
 * Honest balance (DA-8): error → «—» (no fake number), loading → «···», success →
 *       formatKopecks(available_kopecks). Shares the ['earnings','balance'] query cache.
 *
 * Uses:
 *   - components/atoms:Mark
 *   - api/notifications:useNotifications (shared unread cache)
 *   - api/client:apiFetch (balance — shared queryKey ['earnings','balance'])
 *   - lib/format:formatKopecks
 *   - styles/tokens:P, S, G, T
 *
 * Used by:
 *   - screens/HomeScreen/index
 *
 * Glossary: ai/glossary/buyer.md
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { useNotifications } from '../../api/notifications';
import { Mark } from '../../components/atoms';
import { formatKopecks } from '../../lib/format';
import { P, S, G, T } from '../../styles/tokens';

// Minimal slice of the balance view — inline per L-240 (never import api/types.ts).
interface BalanceView {
  available_kopecks: number;
}

function Bell({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill={color}>
      <path d="M12 22a2.5 2.5 0 002.45-2H9.55A2.5 2.5 0 0012 22zm7-5l-1.6-1.6c-.26-.26-.4-.6-.4-.97V11a5 5 0 00-4-4.9V5a1 1 0 10-2 0v1.1A5 5 0 007 11v3.43c0 .37-.14.71-.4.97L5 17v1h14v-1z" />
    </svg>
  );
}

export function HomeHeader() {
  const navigate = useNavigate();
  const { data: notifData } = useNotifications();
  const unread = notifData?.unread_count ?? 0;

  // queryKey ['earnings','balance'] shared with WalletScreen — no extra request.
  const { data, isLoading, isError } = useQuery<BalanceView>({
    queryKey: ['earnings', 'balance'],
    queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
    retry: false,
  });
  // DA-8 — never invent a balance. Only a confirmed fetch shows a ₽ amount.
  const amountLabel = isError ? '—' : isLoading ? '···' : formatKopecks(data?.available_kopecks ?? 0);

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        padding: '10px 16px 10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        ...G.light,
        borderRadius: 0,
        border: 'none',
        boxShadow: 'inset 0 -0.5px 0 rgba(60,50,35,0.08)',
      }}
    >
      {/* Left: mark + wordmark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Mark size={26} />
        <span style={{ font: T.headline, color: S.label }}>Awardy</span>
      </div>

      {/* Right: balance pill + bell */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* SYMMETRIC LIMIT (ADR-022 §6) — `data-coach="wallet"` mirrors COACH_TARGETS.wallet
            in screens/onboarding/coachmarks.ts (peers: RecommendationsList "feed", bell below).
            FTR-1114 coachmarks tour anchor — the balance pill is the «копилка» spotlight. */}
        <button
          data-coach="wallet"
          type="button"
          aria-label="Копилка"
          onClick={() => navigate('/wallet')}
          style={{
            border: 'none',
            cursor: 'pointer',
            padding: '0 12px',
            height: 36,
            borderRadius: 18,
            background: P.ink,
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: T.fontBody,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: -0.1,
          }}
        >
          <span style={{ opacity: 0.6, fontSize: 11, fontWeight: 500 }}>копилка</span>
          <span>{amountLabel}</span>
        </button>

        {/* `data-coach="bell"` — see SYMMETRIC LIMIT note above (FTR-1114 tour anchor). */}
        <button
          data-coach="bell"
          type="button"
          aria-label="Уведомления"
          onClick={() => navigate('/inbox')}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            ...G.light,
          }}
        >
          <Bell color={P.ink} />
          {unread > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                minWidth: 14,
                height: 14,
                borderRadius: 7,
                background: P.terracotta,
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px',
                border: `2px solid ${P.cream}`,
                boxSizing: 'content-box',
              }}
            >
              {unread}
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
